import ReservationCard from '../ReservationCard';
import Overlap from '../Overlap';
import Blinker from '../Blinker';
import { reservations } from 'App/ReservationBook/TestData';
import Typography from 'Components/Atoms/Typography';
import { table } from 'node:console';
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useLayoutEffect,
} from 'react';
import {
  Reservation,
  Table as TableType,
} from 'types/reservations';
import { classHelper, noop, timeString, timeToBlock } from 'utils/helper';
import { TimeTable, ReservabilityMap } from '../..';
import { CircularProgress, Container } from '@material-ui/core';
import CurrentTime from '../CurrentTime';
import { ReservationActionTypes } from 'Contexts/ReservationContext';
import {
  Lock,
  LockOpen,
  PartlyLocked,
  ExpandMore,
  ChevronRight,
  Block,
} from 'Components/Atoms/Icons';

type Blinker = {
  height: number;
  width: number;
  top: number;
  left: number;
  id: string;
};

const TableRow = ({
  table,
  children,
  isActive,
  onClick,
  domId,
  laneIndex = 0,
  totalLanes = 1,
  blocked = false,
  onToggleBlocked,
}: {
  table: TableType;
  children: React.ReactNode;
  isActive: boolean;
  onClick: VoidFunction;
  /**
   * DOM id to apply to this row. Defaults to `table-${name}` (lane 0). For
   * shared-table continuation rows (lanes ≥ 1) the caller passes a
   * lane-suffixed id like `table-${name}__lane-1`.
   */
  domId?: string;
  /** Which concurrent lane this row represents for a shared table. 0 = primary. */
  laneIndex?: number;
  /** Total concurrent lanes rendered for this physical table. */
  totalLanes?: number;
  /** Whether this physical table currently has a per-day block applied. */
  blocked?: boolean;
  /** Toggle the per-day block on this table. Only rendered for lane 0. */
  onToggleBlocked?: () => void;
}) => {
  const blockLabel = blocked ? 'Unblock table' : 'Block table';

  const onDragOver = (e: any) => {
    e.preventDefault();

    const track = document.getElementById('trackRect');

    if (!track) return;

    // All lanes of the same physical table share the same `table.name`, so
    // dragging onto ANY lane correctly attributes the drop to that table.
    track.setAttribute('currentTable', table.name);
    track.setAttribute('currentSpace', table.space);

    // Move the source card vertically so it visually snaps onto the row
    // currently under the cursor. Skipped in add mode (where draggedRes is
    // just the resId, not a card's DOM id).
    const res = document.getElementById(
      track.getAttribute('draggedRes') || ''
    );
    if (!res) return;

    const container = document
      .getElementById('trackRect')
      ?.getBoundingClientRect();
    const rect = e.currentTarget.getBoundingClientRect();
    if (!container || !rect) return;

    res.style.top = String(rect.top - container.top + 1) + 'px';
  };

  const classNames = classHelper([
    isActive && 'active',
    !table.occassions.length && 'blockedTable',
    laneIndex > 0 && 'sharedLaneRow',
  ]);

  const isContinuation = laneIndex > 0;

  return (
    <tr
      id={domId ?? `table-${table.name}`}
      className={classNames}
      onClick={onClick}
      onDragOver={onDragOver}
    >
      <th
        style={{
          paddingLeft: 16,
          whiteSpace: 'nowrap',
          maxWidth: 86,
          overflow: 'hidden',
        }}
      >
        {isContinuation ? (
          // Continuation lane of a shared table: muted marker that visually
          // groups this row under its primary row without repeating the
          // table name.
          <Typography
            variant="text-4"
            color="subdued"
            translation={null}
            style={{ opacity: 0.45 }}
          >
            ↳
          </Typography>
        ) : (
          <>
            <Typography
              variant="text-4"
              color="subdued"
              weight="bold"
              className="mg-r-sm"
            >
              {table.name}
            </Typography>
            {onToggleBlocked && (
              <button
                type="button"
                className={classHelper([
                  'tableRow-block-btn',
                  blocked && 'tableRow-block-btn--blocked',
                ])}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleBlocked();
                }}
                aria-label={blockLabel}
                title={blockLabel}
              >
                {blocked ? (
                  <LockOpen fontSize="small" />
                ) : (
                  <Block fontSize="small" />
                )}
              </button>
            )}
            <Typography variant="text-4" color="subdued">
              {table.max} Pax
            </Typography>
            {totalLanes > 1 && (
              <Typography
                variant="text-4"
                color="subdued"
                translation={null}
                style={{ opacity: 0.55, marginLeft: 6 }}
              >
                ×{totalLanes}
              </Typography>
            )}
          </>
        )}
      </th>
      {children}
    </tr>
  );
};

type TableProps = {
  data: TimeTable;
  index: number;
  activeReservation: null | string;
  activeTable: null | string;
  onTableClick: (id: string) => void;
  onReservationClick: (id: string) => void;
  trackRect: null | DOMRect;
  occassions: { id: string; title: string; hidden?: boolean }[];
  handleReservationAction: (
    id: string,
    action: ReservationActionTypes,
    payload?: any
  ) => void;
  currentTime: number | null;
  reservabilityMap: ReservabilityMap;
  allTables: TableType[];
  onRequestRemoveTable: (
    resId: string,
    newTables: string[],
    space: string,
    thisTable: string
  ) => void;
  collapsed: boolean;
  onToggleCollapse: (spaceId: string) => void;
  spaceLoading: boolean;
  onToggleSpaceClosed: (spaceId: string) => void;
  onToggleTableBlocked: (tableName: string) => void;
  blockedTableNames: Set<string>;
  directControlsDisabled?: boolean;
  blockWidth: number;
};

const Table = ({
  data,
  index,
  activeReservation,
  activeTable,
  onTableClick = noop,
  trackRect = null,
  occassions = [],
  handleReservationAction,
  currentTime,
  onReservationClick,
  reservabilityMap,
  allTables,
  onRequestRemoveTable,
  collapsed,
  onToggleCollapse,
  spaceLoading,
  onToggleSpaceClosed,
  onToggleTableBlocked,
  blockedTableNames,
  directControlsDisabled = false,
  blockWidth,
}: TableProps) => {
  // Group / guest totals for the badge on the right of the header. We reuse
  // the same this-space filter we apply to the cards below so the numbers
  // shown in the header match what is rendered on the grid.
  const { groupCount, guestCount } = useMemo(() => {
    const thisSpaceTableNames = new Set(data.tables.map((t) => t.name));
    const spaceReservations = data.reservations.filter(
      (r) =>
        !r.blocked &&
        (r.tables ?? []).some((t) => thisSpaceTableNames.has(t))
    );
    const guests = spaceReservations.reduce(
      (acc, cV) => acc + (cV.guests || 0),
      0
    );
    return { groupCount: spaceReservations.length, guestCount: guests };
  }, [data.reservations, data.tables]);
  /**
   * Per physical table, compute concurrent-reservation "lane" assignments
   * and derive an expanded row list. For non-shared tables the expanded
   * list contains exactly one entry (laneIndex=0) and the DOM id matches
   * the previous `table-${name}`, preserving backwards compatibility with
   * drag-over / card positioning / CurrentTime lookups.
   *
   * For shared tables we run the classic calendar-event layout algorithm
   * (same one in use at TableSideBar/Components/TableSchedule.tsx), then
   * emit one row per lane. Concurrent cards end up on different rows, so
   * the existing overlap detection (which compares `position.top`) stops
   * flagging them — no striped warning overlay on legitimate sharing.
   */
  const { tableLanes, expandedRows } = useMemo(() => {
    const tableLanes = new Map<
      string,
      { assignments: Map<string, number>; totalLanes: number }
    >();
    type ExpandedRow = {
      table: TableType;
      laneIndex: number;
      totalLanes: number;
      domId: string;
      key: string;
    };
    const expandedRows: ExpandedRow[] = [];

    for (const t of data.tables) {
      if (!t.shared) {
        tableLanes.set(t.name, { assignments: new Map(), totalLanes: 1 });
        expandedRows.push({
          table: t,
          laneIndex: 0,
          totalLanes: 1,
          domId: `table-${t.name}`,
          key: t.id,
        });
        continue;
      }

      // Only reservations that actually occupy THIS physical table drive its
      // lane count — a combo on tables [A, B] contributes to both A and B
      // but adds one lane to each independently.
      const relevant = data.reservations
        .filter(
          (r) =>
            !r.blocked &&
            r.status !== 'failed' &&
            (r.tables ?? []).includes(t.name) &&
            typeof r.startTimeInBlocks === 'number' &&
            typeof r.endTimeInBlocks === 'number'
        )
        .sort(
          (a, b) => (a.startTimeInBlocks ?? 0) - (b.startTimeInBlocks ?? 0)
        );

      const laneEnds: number[] = [];
      const assignments = new Map<string, number>();
      for (const r of relevant) {
        const start = r.startTimeInBlocks ?? 0;
        const end = r.endTimeInBlocks ?? 0;
        let laneIndex = laneEnds.findIndex((e) => e <= start);
        if (laneIndex === -1) laneIndex = laneEnds.length;
        laneEnds[laneIndex] = end;
        assignments.set(r.id, laneIndex);
      }
      const totalLanes = Math.max(1, laneEnds.length);
      tableLanes.set(t.name, { assignments, totalLanes });

      for (let lane = 0; lane < totalLanes; lane++) {
        expandedRows.push({
          table: t,
          laneIndex: lane,
          totalLanes,
          // Lane 0 keeps the original DOM id so existing callers that look
          // up `table-${name}` (drag-over, CurrentTime, etc.) stay working.
          domId:
            lane === 0 ? `table-${t.name}` : `table-${t.name}__lane-${lane}`,
          key: `${t.id}__lane-${lane}`,
        });
      }
    }

    return { tableLanes, expandedRows };
  }, [data.tables, data.reservations]);

  // Card positions below are computed by reading row `<tr>` rects from the
  // DOM. When `expandedRows` changes (e.g. shared-table lanes appear), the
  // reservationCards memo runs in the same render BEFORE React has committed
  // the new rows, so `getElementById` misses and cards land off-screen. Bump
  // a version after commit so the memo re-runs against the fresh DOM.
  const [domVersion, setDomVersion] = useState(0);
  useLayoutEffect(() => {
    setDomVersion((v) => v + 1);
  }, [expandedRows]);

  const blocks = useMemo(() => {
    let [start, close] = [data.start, data.close];

    if (start === null || close === null || start >= close || isNaN(start)) {
      return [];
    } else {
      return Array.from(Array(Math.max(close - start, 0)), (_, i) => ({
        block: i + (start || 0),
        timeString: timeString({ block: i + (start || 0) }),
      }));
    }
  }, [data]);

  const { reservationCards, overlaps } = useMemo(() => {
    let reservationCards: {
      id: string;
      table: string;
      position: {
        top: number;
        left: number;
        width: number;
        height: number;
      };
      reservation: Reservation;
    }[] = [];

    let overlaps: {
      top: number;
      left: number;
      width: number;
      height: number;
    }[] = [];

    if (!trackRect || !blocks.length) return { reservationCards, overlaps };

    const accurateTrackRect =
      document.getElementById('trackRect')?.getBoundingClientRect() ||
      trackRect;

    // Only render cards for tables that belong to THIS space-group. The
    // upstream filter at TimeTable/index.tsx is intentionally loose and a
    // single reservation can appear in every space's `data.reservations`.
    // Without this filter we end up with duplicate cards (same DOM id) at
    // the same row across multiple Table components, which breaks drag
    // tracking because getElementById only finds the first one.
    const thisSpaceTableNames = new Set(data.tables.map((t) => t.name));

    reservationCards = data.reservations
      // Drop day-blockers — these are synthetic reservations used only
      // to mark a table as unavailable. The block is conveyed by the
      // greyed-out cells in the grid (via the reservability map); we
      // don't want a card-looking pill rendered for them.
      .filter((res) => !res.blocked)
      .map((res) => {
        return {
          ...res,
          positions: (res?.tables ?? [])
            .filter((t) => thisSpaceTableNames.has(t))
            .map((t) => {
            // For shared tables the card snaps onto the row of its
            // assigned lane so concurrent reservations don't stack.
            // For non-shared tables (or lane-0 shared) the DOM id is
            // the original `table-${name}`.
            const lane = tableLanes.get(t)?.assignments.get(res.id) ?? 0;
            const rowDomId =
              lane === 0 ? `table-${t}` : `table-${t}__lane-${lane}`;
            let tableRect =
              document.getElementById(rowDomId)?.getBoundingClientRect() ||
              null;

            let top = (tableRect?.top || 0) - accurateTrackRect.top + 1;

            let start = res.startTimeInBlocks || timeToBlock(res.time);

            let left = (start - blocks[0].block) * blockWidth + 88;

            let reservationLength =
              !res || res.reservationLength < 0
                ? res.endTimeInBlocks - res.startTimeInBlocks
                : res.reservationLength;

            return {
              id: `${res.id}__${t}`,
              table: t,
              top,
              left,
              width: blockWidth * (reservationLength || 4) - 1,
              height: 23,
            };
          }),
        };
      })
      .reduce(
        (
          acc: {
            id: string;
            table: string;
            position: {
              top: number;
              left: number;
              width: number;
              height: number;
            };
            reservation: Reservation;
          }[],
          { positions, ...reservation }
        ) => {
          return [
            ...acc,
            ...positions.map(({ id, table, ...position }) => ({
              id,
              table,
              position,
              reservation,
            })),
          ];
        },
        []
      );

    overlaps = reservationCards.reduce((acc: any[], cV, i, arr) => {
      let matches = arr
        .map((r, i2) => {
          if (i2 <= i) return false;

          if (r.position.top !== cV.position.top) return false;

          let a1 = r.position.left,
            a2 = r.position.left + r.position.width,
            b1 = cV.position.left,
            b2 = cV.position.left + cV.position.width;

          const left = Math.max(a1, b1);
          const right = Math.min(a2, b2);

          // Negated form so a NaN edge from a broken time also bails out.
          if (!(right > left)) return false;

          // Keep a band that spans a card end to end translucent, so the
          // card underneath stays readable.
          const coversWholeCard =
            (left === a1 && right === a2) || (left === b1 && right === b2);

          return {
            ...cV.position,
            left,
            width: right - left,
            opacity: coversWholeCard ? 0.5 : undefined,
          };
        })
        .filter((o) => !!o);

      return [...acc, ...matches];
    }, []);
    return { reservationCards, overlaps };
  }, [trackRect, data.reservations, blocks, blockWidth, tableLanes, domVersion]);

  const [blinkers, setblinkers] = useState<Blinker[]>([]);

  const handleDragStart = (e: any) => {
    const track = document.getElementById('trackRect');

    if (!track) return;

    const trackRect = track.getBoundingClientRect();
    const sourceCard = e.currentTarget as HTMLElement;
    const rect = sourceCard.getBoundingClientRect();

    const tables = document.querySelectorAll('#trackRect table');

    const arr: Blinker[] = [];

    tables.forEach((node, i) => {
      const { height, top, left, width } = node.getBoundingClientRect();

      arr.push({
        id: i + 'left',
        left: left + 70 - trackRect.left,
        top: top - trackRect.top,
        height,
        width: rect.left - left - 70,
      });

      arr.push({
        id: i + 'right',
        left: rect.left + rect.width - trackRect.left,
        top: top - trackRect.top,
        height,
        width: left + width - (rect.left + rect.width),
      });
    });

    setblinkers(arr);

    e.dataTransfer.setData('text', sourceCard.id);
    track.setAttribute('draggedRes', sourceCard.id);

    // Use a fully-invisible 1×1 drag image so the browser doesn't overlay
    // its default translucent snapshot of the source card — only the real
    // source card (moved via onDragOver) is visible during the drag.
    const invisible = document.createElement('div');
    invisible.style.position = 'fixed';
    invisible.style.top = '-1000px';
    invisible.style.left = '-1000px';
    invisible.style.width = '1px';
    invisible.style.height = '1px';
    invisible.style.opacity = '0';
    invisible.style.pointerEvents = 'none';
    document.body.appendChild(invisible);
    e.dataTransfer.setDragImage(invisible, 0, 0);
    requestAnimationFrame(() => {
      if (invisible.parentNode) invisible.parentNode.removeChild(invisible);
    });
  };

  const handleDragEnd = (_e: any) => {
    const track = document.getElementById('trackRect');
    if (track) {
      const draggedResId = track.getAttribute('draggedRes');
      if (draggedResId) {
        const el = document.getElementById(draggedResId);
        if (el) {
          // The source card was moved vertically via onDragOver; snap it
          // back to its React-controlled position. If the drop succeeded
          // the component will re-render anyway, but on a cancelled drag
          // this prevents the card from being left on the wrong row.
          const cardData = reservationCards.find((r) => r.id === draggedResId);
          if (cardData) {
            el.style.top = cardData.position.top + 'px';
          } else {
            el.style.top = '';
          }
          el.style.visibility = '';
        }
      }
      track.removeAttribute('draggedRes');
      track.removeAttribute('currentTable');
      track.removeAttribute('currentSpace');
      track.removeAttribute('dragMode');
    }
    setblinkers([]);
  };

  const handleRemoveTable = (resId: string, thisTable: string) => {
    const res = data.reservations.find((r) => r.id === resId);
    if (!res || !res.tables || res.tables.length <= 1) return;
    const newTables = res.tables.filter((t) => t !== thisTable);
    onRequestRemoveTable(resId, newTables, res.space, thisTable);
  };

  const handleAddModeDragStart = (e: React.DragEvent, resId: string) => {
    e.stopPropagation();

    const track = document.getElementById('trackRect');
    if (!track) return;

    e.dataTransfer.setData('text', resId);
    track.setAttribute('draggedRes', resId);
    track.setAttribute('dragMode', 'add');

    // Clone one of the reservation's existing cards to use as a visible drag
    // preview that follows the cursor. Without this the user would only see
    // the tiny "+" icon being dragged.
    const sourceCard = document.querySelector<HTMLElement>(
      `[id^="${CSS.escape(resId)}__"]`
    );

    if (sourceCard) {
      const width = sourceCard.offsetWidth;
      const height = sourceCard.offsetHeight;

      const clone = sourceCard.cloneNode(true) as HTMLElement;
      clone.removeAttribute('id');
      clone.style.position = 'fixed';
      clone.style.top = '-9999px';
      clone.style.left = '-9999px';
      clone.style.width = width + 'px';
      clone.style.height = height + 'px';
      clone.style.pointerEvents = 'none';
      clone.style.opacity = '0.9';

      // The cloned card still has the per-card action buttons inside it;
      // strip them from the preview so it reads as a clean reservation card.
      const actions = clone.querySelector('.resCardActions');
      if (actions && actions.parentNode) actions.parentNode.removeChild(actions);

      document.body.appendChild(clone);
      // Anchor the cursor near the right edge of the preview so the card
      // trails to the left of the cursor — the user grabbed the "+" handle
      // on the right of the card, so that feels natural.
      e.dataTransfer.setDragImage(
        clone,
        Math.max(width - 10, 0),
        Math.floor(height / 2)
      );

      // Browsers snapshot the element synchronously after setDragImage; remove
      // the clone on the next frame so it never flashes on screen.
      requestAnimationFrame(() => {
        if (clone.parentNode) clone.parentNode.removeChild(clone);
      });
    }
  };

  const onDrop = (e: any) => {
    let track = document.getElementById('trackRect');

    setblinkers([]);

    if (!track) return;

    let draggedRes = track.getAttribute('draggedRes') || '';
    let newTable = track.getAttribute('currentTable');
    let space = track.getAttribute('currentSpace');
    let mode = track.getAttribute('dragMode');

    track.removeAttribute('draggedRes');
    track.removeAttribute('currentTable');
    track.removeAttribute('currentSpace');
    track.removeAttribute('dragMode');

    if (mode === 'add') {
      const resId = draggedRes;
      const res = data.reservations.find((r) => r.id === resId);

      if (!res || !res.tables) return;
      if (!newTable) return;

      const newTables = Array.from(new Set([...res.tables, newTable]));

      // Already present: no-op
      if (newTables.length === res.tables.length) return;

      // Multi-space guard using flat allTables
      const spaces = allTables
        .filter((t) => newTables.includes(t.name))
        .reduce<string[]>(
          (acc, cV) => [...new Set([...acc, cV.space])],
          []
        );

      if (spaces.length > 1) {
        // @TODO Show warning Message
        return;
      }

      handleReservationAction(resId, ReservationActionTypes.CHANGE_TABLE, {
        tables: newTables,
        space: space || res.space,
      });
      return;
    }

    // Swap path (existing behaviour)
    let [resId, oldTable] = draggedRes.split('__');

    let { tables, space: oldSpace } =
      data.reservations.find((r) => r.id === resId) || {};

    if (!tables) return;

    // Guard against invalid drop targets or no-op moves
    if (!newTable || newTable === oldTable) return;

    let newTables = [
      ...new Set(tables.map((t) => (t === oldTable ? newTable : t))),
    ];

    if (newTables.length > 1) {
      let spaces = allTables
        .filter((t) => newTables.includes(t.name))
        .reduce((acc: string[], cV) => [...new Set([...acc, cV.space])], []);

      if (spaces.length > 1) {
        // @TODO Show warning Message

        return;
      }
    }
    handleReservationAction(resId, ReservationActionTypes.CHANGE_TABLE, {
      tables: newTables,
      space: space || oldSpace,
    });
  };

  const showCurrentTime =
    (!!data.start || data.start === 0) &&
    !!data.close &&
    currentTime !== null &&
    currentTime >= data.start &&
    currentTime <= data.close;

  return (
    <div
      className={classHelper([
        data.closed && 'closedSpace',
        collapsed && 'collapsedSpace',
      ])}
      style={{ marginBottom: 10 }}
      onDrop={onDrop}
      onDragOver={(e) => e.preventDefault()}
    >
      <div className="header-Container" onDragOver={(e) => e.preventDefault()}>
        <div className="spaceHeader-left-group">
          <button
            type="button"
            className="spaceHeader-toggle"
            onClick={() => onToggleCollapse(data.id)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand space' : 'Collapse space'}
          >
            <span className="spaceHeader-chevron">
              {collapsed ? (
                <ChevronRight fontSize="small" />
              ) : (
                <ExpandMore fontSize="small" />
              )}
            </span>
            <Typography variant="h5" className="spaceHeader-title">
              {data.title}
            </Typography>
          </button>
          <button
            type="button"
            className="spaceHeader-lock-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (!spaceLoading && !directControlsDisabled) {
                onToggleSpaceClosed(data.id);
              }
            }}
            disabled={spaceLoading || directControlsDisabled}
            aria-label={
              data.closedState === 2
                ? 'Bereich geschlossen'
                : data.closedState === 1
                ? 'Bereich teilweise geschlossen'
                : 'Bereich geöffnet'
            }
          >
            {spaceLoading && <CircularProgress size={18} />}
            {!spaceLoading && data.closedState === 2 && (
              <Lock fontSize="small" className="lockIcon lockIcon--closed" />
            )}
            {!spaceLoading && data.closedState === 1 && (
              <PartlyLocked
                fontSize="small"
                className="lockIcon lockIcon--partial"
              />
            )}
            {!spaceLoading && data.closedState === 0 && (
              <LockOpen fontSize="small" className="lockIcon lockIcon--open" />
            )}
          </button>
        </div>
        <div className="spaceHeader-right">
          <Typography variant="text-4" color="subdued" translation={null}>
            {`${groupCount} groups · ${guestCount} Pax`}
          </Typography>
        </div>
      </div>
      {collapsed ? null : (
      <>
      <table className="time__table" id={'tables-' + String(index)}>
        <tbody>
          <tr className="times" onDragOver={(e) => e.preventDefault()}>
            <th></th>
            {blocks.map((b) => (
              <td key={b.block}>
                <Typography variant="text-4" color="subdued" translation={null}>
                  {b.timeString}
                </Typography>
              </td>
            ))}
          </tr>
          {expandedRows.map((row) => (
            <TableRow
              key={row.key}
              table={row.table}
              isActive={activeTable === row.table.id}
              onClick={() => onTableClick(row.table.id)}
              domId={row.domId}
              laneIndex={row.laneIndex}
              totalLanes={row.totalLanes}
              blocked={blockedTableNames.has(row.table.name)}
              onToggleBlocked={
                row.laneIndex === 0 && !directControlsDisabled
                  ? () => onToggleTableBlocked(row.table.name)
                  : undefined
              }
            >
              {blocks.map((b) => (
                <td
                  key={b.block}
                  className={
                    reservabilityMap[row.table.name]?.has(b.block)
                      ? 'cellNotReservable'
                      : undefined
                  }
                />
              ))}
            </TableRow>
          ))}
        </tbody>
      </table>
      {reservationCards.map((r) => (
        <ReservationCard
          key={r.id}
          id={r.id}
          reservation={r.reservation}
          position={r.position}
          occassions={occassions}
          selected={activeReservation === r.reservation.id}
          onClick={(e) => {
            e.stopPropagation();
            onReservationClick(r.reservation.id);
          }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          thisTable={r.table}
          canRemove={(r.reservation.tables?.length ?? 0) > 1}
          onRemoveTable={handleRemoveTable}
          onAddTableDragStart={handleAddModeDragStart}
          onAddTableDragEnd={handleDragEnd}
        />
      ))}
      {overlaps.map((o, i) => (
        <Overlap key={i} position={o} />
      ))}
      {blinkers.map(({ id, ...position }) => (
        <Blinker key={id} position={position} />
      ))}

      {(!!data.start || data.start === 0) && showCurrentTime && (
        <CurrentTime
          currentTime={currentTime}
          start={data.start}
          associatedTableId={'tables-' + String(index)}
          trackRect={trackRect}
          id={data.id + '-currentTime'}
          index={index}
          blockWidth={blockWidth}
        />
      )}
      </>
      )}
    </div>
  );
};

export default Table;
